import { useMemo } from 'react';
import { t } from '@lingui/macro';
import {
  createStaticMapsUrl,
  StaticMapsLocation,
  StaticMapsMarker,
} from '@vis.gl/react-google-maps';
import styled from 'styled-components';

import { Box } from '@rover/kibble/core';
import type { SearchResult } from '@rover/types/src/apiResponses/FetchSearchResponse';
import emitMetric from '@rover/utilities/emitMetric';

import { GEOMAP_CLOUD_ID } from './GeoMap';
import { DEFAULT_MAP_ZOOM } from './types';

const StyledMapImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

type Props = {
  visible: boolean;
  results: SearchResult[];
  staticGoogleMapsApiKey: string;
  center:
    | {
        lat: number | string;
        lng: number | string;
      }
    | null
    | undefined;
  zoom: number | null | undefined;
};

const GeoMapStatic = ({
  results,
  visible,
  staticGoogleMapsApiKey,
  center,
  zoom,
}: Props): JSX.Element => {
  const staticMapsUrl = useMemo(() => {
    if (!visible) return '';
    const markers = results.map(
      (result) =>
        ({
          location: { lat: parseFloat(result.latitude), lng: parseFloat(result.longitude) },
          icon: 'https://www.rover.com/static/images/map-marker.png',
        } as StaticMapsMarker)
    );
    let url = createStaticMapsUrl({
      apiKey: staticGoogleMapsApiKey,
      center: center as StaticMapsLocation,
      zoom: zoom || DEFAULT_MAP_ZOOM,
      width: 1080,
      height: 1080,
      markers,
      mapId: GEOMAP_CLOUD_ID,
    });
    const urlObj = new URL(url);
    urlObj.searchParams.delete('key');
    url = urlObj.toString();
    url = url.replace('https://maps.googleapis.com/maps/api/staticmap', '/static-map-signature/');
    return url;
  }, [center, results, staticGoogleMapsApiKey, visible, zoom]);

  return (
    <Box
      display={!visible ? 'none' : 'block'}
      position="absolute"
      zIndex="1"
      width="100%"
      height="100%"
    >
      {staticMapsUrl !== '' && (
        <StyledMapImage
          src={staticMapsUrl}
          alt={t`Map showing search results`}
          onLoad={() => emitMetric('search_page_map_static_load', { failed: 'false' })}
          onError={() => emitMetric('search_page_map_static_load', { failed: 'true' })}
        />
      )}
    </Box>
  );
};

export default GeoMapStatic;
